// Stage 3A:WordPress 接続 API
// Stage 3B:本文件独立出来(从 admin.ts 拆出),site 相关端点集中管理
// Stage Pages-1:追加 fetchSitePages — WordPress 固定ページ一覧 read-only proxy

import { apiFetch } from "./client";

export interface SyncMeta {
  stale: boolean;
  sync_status: string | null;
  last_synced_at: string | null;
  last_error: string | null;
  ttl_seconds: number;
}

export interface WordPressConnection {
  site_url: string;
  username: string;
  display_name: string | null;
  is_active: boolean;
  has_application_password: boolean;
  application_password_hint: string | null;
  last_tested_at: string | null;
  last_test_status: string | null;
  last_test_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface WordPressTestResult {
  status: "connected" | "auth_failed" | "not_found" | "timeout" | "error";
  message: string;
  wp_user: { id: number; name: string } | null;
  mode: "input" | "stored";
}

export interface WordPressConnectionUpsertBody {
  site_url: string;
  username: string;
  application_password: string;
  display_name?: string | null;
}

// Stage Pages-1:固定ページ一覧 read-only。
// 後端 GET /api/site/pages が {pages: [...], total: int} を返す。
// pages 要素は後端 site_pages_service._normalize_page() の出力:
//   {wordpress_page_id, title, status, slug, link, modified, date}
export interface SitePageSummary {
  wordpress_page_id: number;
  title: string;
  status: string;
  slug: string;
  link: string | null;
  modified: string | null;
  date: string | null;
}

export interface SitePagesResponse {
  pages: SitePageSummary[];
  total: number;
  sync_meta?: SyncMeta;
}

export const siteApi = {
  getConnection: () => apiFetch<WordPressConnection>("/site/wordpress/connection"),

  upsertConnection: (body: WordPressConnectionUpsertBody) =>
    apiFetch<WordPressConnection>("/site/wordpress/connection", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteConnection: () =>
    apiFetch<{ status: "deleted" }>("/site/wordpress/connection", {
      method: "DELETE",
    }),

  testConnection: (body: {
    site_url: string;
    username: string;
    application_password?: string;
  }) =>
    apiFetch<WordPressTestResult>("/site/wordpress/test", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Stage Pages-1:GET /api/site/pages → {pages: SitePageSummary[], total: number}
  // envelope は {success, data, error} で、apiFetch が data を返すので
  // response は {pages, total} の形。
  fetchSitePages: () => fetchSitePages(),

  refreshPages: (force = true) =>
    apiFetch<{ refreshed: string[]; skipped: string[] }>("/site/pages/refresh", {
      method: "POST",
      body: JSON.stringify({ force }),
    }),
};

// 単独 export 版(後方互換 + tree-shake されやすく)。
// /api/site/pages のレスポンス envelope を apiFetch が剥がし、data 部分だけ返す:
//   data = {pages: SitePageSummary[], total: number}
export async function fetchSitePages(): Promise<SitePagesResponse> {
  return apiFetch<SitePagesResponse>("/site/pages");
}
